import { motion } from 'motion/react'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { useOptionalSessionLanguage } from '@app/context/SessionSettingsContext'

import { Body2, Caption1 } from '@ui/Typography/Typography'
import { CloseIcon } from '@ui/icons/Close.icon'

import { useWindowResize } from '@utils/domUtils'

import { TickmillProductType } from '@model/TickmillProductType'

import { Locale } from '@global/locale/Locale'

import { useTourtipPosition } from '@hooks/useTourtipPosition'

import { HighlightMask } from './HighlightMask'
import { TourActions } from './TourActions'
import { TourtipArrowIcon } from './TourtipArrow.icon'

import styles from './Tourtip.module.scss'

export type Placement =
  | 'top-center'
  | 'bottom-center'
  | 'left-center'
  | 'right-center'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'left-top'
  | 'left-bottom'
  | 'right-top'
  | 'right-bottom'
  | 'auto'

export type StepKind = 'intro' | 'regular' | 'outro'

export type TargetRef = { kind: 'data-attr'; key: string } | { kind: 'virtual' }

export interface TourImage {
  key?: string
  localeMap?: Record<string, string>
}

export interface TourStep {
  id: string
  kind: StepKind
  target: TargetRef
  preferredPlacement?: Placement | { mobile: Placement | null; desktop: Placement }
  header: string
  description: string
  descriptionMobile?: string
  image?: TourImage
  device?: 'desktop' | 'mobile'
  primaryActionText?: string
  secondaryActionText?: string
  primaryActionCommand?: string
  secondaryActionCommand?: string
  disablePointer?: boolean
  highlightGrow?: { mobile?: number; desktop?: number }
  condition?: boolean
}

export interface TourDefinition {
  id: string
  version: number
  products: TickmillProductType[]
  steps: TourStep[]
}

interface Props {
  def: TourDefinition
  onClose: () => void
  onComplete?: () => void
  onCommand: (cmd: string | undefined) => void
  locale?: Locale
}

export const Tourtip: FC<Props> = ({
  def,
  onClose,
  onComplete,
  onCommand,
  locale: forcedLocale,
}) => {
  const [index, setIndex] = useState(0)
  const [visible, setVisible] = useState(true)
  const lastStepIdRef = useRef<string | undefined>(undefined)

  const step = def.steps[index]

  const sessionLocale = useOptionalSessionLanguage()
  const locale = forcedLocale ?? sessionLocale ?? 'en'

  const { t } = useTranslation()

  const isMobile = useWindowResize()

  const goNext = useCallback(
    () => setIndex((i) => Math.min(i + 1, def.steps.length - 1)),
    [def.steps.length]
  )
  const goPrev = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), [def.steps.length])

  const { boxRef, style: boxStyle, pointerPlacement, targetRect } = useTourtipPosition(step)

  const variants = {
    open: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1] as const,
      },
    },
    closed: {
      opacity: 0,
      transition: {
        duration: 0.2,
        ease: [0.4, 0, 0.2, 1] as const,
      },
    },
  }

  const getTipDescription = () => {
    if (isMobile && step.descriptionMobile) {
      return t(`${step.descriptionMobile}`)
    }

    return t(`${step.description}`)
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        goNext()
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        goPrev()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, goNext, goPrev])

  useEffect(() => {
    const currentId = step?.id
    if (lastStepIdRef.current === undefined) {
      lastStepIdRef.current = currentId
      return
    }
    if (currentId === lastStepIdRef.current) return
    lastStepIdRef.current = currentId
    setVisible(false)
    const animationFrameId = requestAnimationFrame(() => {
      setVisible(true)
    })
    return () => cancelAnimationFrame(animationFrameId)
  }, [step?.id])

  if (!step) return null

  const getTourImage = (img?: TourImage) => {
    if (!img) return undefined
    const key = img.localeMap?.[locale] ?? img.key
    if (!key) return undefined
    return key
  }

  const pointer =
    step.kind === 'regular' && pointerPlacement && !isMobile ? (
      <div className={styles.pointer} aria-hidden='true'>
        <TourtipArrowIcon />
      </div>
    ) : null

  const overlay =
    step.kind === 'regular' ? (
      <HighlightMask
        rect={targetRect}
        radius={8}
        onScrimClick={onClose}
        grow={step.highlightGrow}
      />
    ) : (
      <div onClick={() => onClose()} className={styles.overlay} />
    )

  const box = (
    <div
      ref={boxRef}
      className={styles.tourtip}
      style={boxStyle}
      data-pointer-placement={pointerPlacement || undefined}
    >
      {pointer}
      <motion.div
        key={step.id}
        className={styles.tip}
        variants={variants}
        initial='closed'
        animate={visible ? 'open' : 'closed'}
      >
        {step.image && step.image.key && (
          <img src={t(`${getTourImage(step.image)}`)} alt={t(step.header)} />
        )}
        <div className={styles.iconButton} onClick={onClose}>
          <CloseIcon size={14} color='text-white' />
        </div>
        <div className={styles.text}>
          <Body2 semibold>{t(`${step.header}`)}</Body2>
          <Caption1>{getTipDescription()}</Caption1>
        </div>
        <div className={styles.actions}>
          <TourActions
            kind={step.kind}
            index={index}
            total={def.steps.length}
            onPrev={goPrev}
            onNext={goNext}
            introActions={{
              secondary: { onClick: onClose, text: step.secondaryActionText },
              primary: { onClick: goNext, text: step.primaryActionText },
            }}
            outroActions={{
              secondary: { onClick: onComplete || onClose, text: step.secondaryActionText },
              primary: {
                onClick: () => onCommand(step?.primaryActionCommand),
                text: step.primaryActionText,
              },
            }}
          />
        </div>
      </motion.div>
    </div>
  )

  return createPortal(
    <>
      {overlay}
      {box}
    </>,
    document.body
  )
}
